import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import KPICard from "@/components/KPICard";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Target, 
  FileCheck, 
  Clock,
  Loader2,
  FileSignature,
  Eye,
  Car,
  Euro
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export default function SellerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, objectivesRes] = await Promise.all([
        api.get("/statistics/dashboard"),
        api.get("/objectives")
      ]);
      setStats(statsRes.data);
      setObjectives(objectivesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: "Brouillon", className: "bg-gray-100 text-gray-700" },
      pending_seller: { label: "À signer", className: "bg-amber-100 text-amber-700" },
      pending_manager: { label: "Attente responsable", className: "bg-blue-100 text-blue-700" },
      signed: { label: "Signé", className: "bg-green-100 text-green-700" }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const monthNames = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  if (loading) {
    return (
      <Layout title="Mes objectifs">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
        </div>
      </Layout>
    );
  }

  const pendingObjectives = objectives.filter(o => o.status === "pending_seller");
  const signedObjectives = objectives.filter(o => o.status === "signed");

  return (
    <Layout title={`Bienvenue, ${user?.name}`}>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-testid="seller-dashboard-kpis">
        <KPICard
          value={stats?.objectives_pending || 0}
          label="À signer"
          icon={Clock}
          color="amber"
        />
        <KPICard
          value={stats?.objectives_signed || 0}
          label="Objectifs signés"
          icon={FileCheck}
          color="green"
        />
      </div>

      {/* Pending Signatures */}
      {pendingObjectives.length > 0 && (
        <div className="mb-8" data-testid="pending-objectives">
          <h2 
            className="text-xl font-bold text-[#212E38] mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Objectifs en attente de signature
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pendingObjectives.map((obj) => (
              <Card key={obj.id} className="border-l-4 border-amber-500">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {monthNames[obj.month]} {obj.year}
                    </CardTitle>
                    {getStatusBadge(obj.status)}
                  </div>
                  <CardDescription>{obj.concession_name}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <Car className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                      <p className="text-2xl font-bold text-[#212E38]">{obj.vn_target}</p>
                      <p className="text-xs text-gray-500">VN</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <Car className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                      <p className="text-2xl font-bold text-[#212E38]">{obj.vo_target}</p>
                      <p className="text-xs text-gray-500">VO</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <Euro className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                      <p className="text-2xl font-bold text-[#212E38]">{obj.financing_target?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Financement</p>
                    </div>
                  </div>
                  <Button
                    className="w-full btn-primary"
                    onClick={() => navigate(`/sign/${obj.id}`)}
                    data-testid={`sign-objective-${obj.id}`}
                  >
                    <FileSignature className="w-4 h-4 mr-2" />
                    Signer mes objectifs
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Signed Objectives */}
      <div data-testid="signed-objectives">
        <h2 
          className="text-xl font-bold text-[#212E38] mb-4"
          style={{ fontFamily: 'Chivo, sans-serif' }}
        >
          Historique des objectifs
        </h2>
        
        {signedObjectives.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {signedObjectives.map((obj) => (
              <Card key={obj.id} className="border-l-4 border-green-500">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {monthNames[obj.month]} {obj.year}
                    </CardTitle>
                    {getStatusBadge(obj.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xl font-bold">{obj.vn_target}</p>
                      <p className="text-xs text-gray-500">VN</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold">{obj.vo_target}</p>
                      <p className="text-xs text-gray-500">VO</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold">{obj.financing_target?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">€</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="empty-state card-dashboard">
            <FileCheck className="empty-state-icon" />
            <p>Aucun objectif signé pour le moment</p>
          </div>
        )}
      </div>

      {/* No objectives at all */}
      {objectives.length === 0 && (
        <div className="empty-state card-dashboard">
          <Target className="empty-state-icon" />
          <p>Vous n'avez pas encore d'objectifs assignés</p>
          <p className="text-sm text-gray-400 mt-2">
            Votre responsable vous enverra vos objectifs mensuels
          </p>
        </div>
      )}
    </Layout>
  );
}
